import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { DocumentTemplate, TemplateCategory, TemplateStatus, TemplateVersion } from "@/data/documentTemplateEngine";

interface DBRow {
  id: string;
  organization_id: string;
  name: string;
  category: string;
  status: string;
  current_version: number;
  versions: TemplateVersion[];
  created_by: string | null;
  last_updated_by: string | null;
  created_at: string;
  updated_at: string;
}

function toTemplate(row: DBRow): DocumentTemplate {
  return {
    id: row.id,
    name: row.name,
    category: row.category as TemplateCategory,
    status: row.status as TemplateStatus,
    currentVersion: row.current_version,
    versions: row.versions ?? [],
    createdAt: row.created_at.slice(0, 10),
    createdBy: row.created_by ?? "System",
    lastUpdatedAt: row.updated_at.slice(0, 10),
    lastUpdatedBy: row.last_updated_by ?? "System",
  };
}

export const DocumentTemplatesService = {
  async getAll(): Promise<DocumentTemplate[]> {
    const { data, error } = await supabase
      .from("document_templates")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data as DBRow[]).map(toTemplate);
  },

  async create(template: DocumentTemplate): Promise<DocumentTemplate> {
    const organizationId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("document_templates")
      .insert({
        id: template.id,
        organization_id: organizationId,
        name: template.name,
        category: template.category,
        status: template.status,
        current_version: template.currentVersion,
        versions: template.versions,
        created_by: template.createdBy,
        last_updated_by: template.lastUpdatedBy,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return toTemplate(data as DBRow);
  },

  async update(id: string, changes: Partial<{
    name: string;
    category: string;
    status: string;
    currentVersion: number;
    versions: TemplateVersion[];
    lastUpdatedBy: string;
  }>): Promise<void> {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (changes.name !== undefined) payload.name = changes.name;
    if (changes.category !== undefined) payload.category = changes.category;
    if (changes.status !== undefined) payload.status = changes.status;
    if (changes.currentVersion !== undefined) payload.current_version = changes.currentVersion;
    if (changes.versions !== undefined) payload.versions = changes.versions;
    if (changes.lastUpdatedBy !== undefined) payload.last_updated_by = changes.lastUpdatedBy;

    const { error } = await supabase
      .from("document_templates")
      .update(payload)
      .eq("id", id);

    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from("document_templates")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
